import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components/macro';
import { NewModal } from 'lib/components';
import { useAuth, VideoListAutomotiveItem } from 'lib/context';
import { Button } from 'react-covideo-common';
import {
  DEFAULT_FOLDER,
  DefaultFolderIds,
  SHARED_FOLDER_PREFIX,
} from 'lib/const/Folders';
import { useVideoMoveCopy } from 'lib/api/videos/useVideoMoveCopy';
import { useVideosMoveCopyMultipleMutation } from 'lib/api/videos/useVideoMoveCopyMultiple';
import { FoldersV2 } from 'lib/api/folders/getFoldersV2';
import {
  ACCESS_FILTER,
  FolderV2Dropdown,
} from '../dropdown/folders/FolderV2Dropdown';
import { Formik } from 'formik';
import { FormikCheckboxField } from '../formik/FormikCheckbox';
import { Flex } from '../styles/layout';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import {
  CovideoGTMVehicleItem,
  CovideoGTMVideoAttributes,
} from 'lib/hooks/GTM/types';
import { GTMVideoInteractionActions } from 'lib/hooks/GTM/actions/video';

const Title = styled.div`
  height: 24px;
  opacity: 0.75;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.71;
  letter-spacing: 0.4px;
  color: #343a40;
  margin-bottom: 8px;
`;

type Props = {
  handleModalClose: () => void;
  videoIds: string[];
  currentFolder?: FoldersV2;
  folder?: FoldersV2;
  containsSharedFolderVideos: boolean;
  selectedVideos: VideoListAutomotiveItem[];
  selectedVideo: VideoListAutomotiveItem | null;
};

export const ModalMoveCopy = (props: Props) => {
  const { selectedVideos, selectedVideo } = props || {};
  const { t } = useTranslation();
  const { trackGTMVideoInteractionEvent } = useGTMAnalytics();

  const { handleModalClose, videoIds, currentFolder, folder } = props;

  const {
    userData: { resellerId, id: userId },
  } = useAuth();
  const walkaroundSocialFolder = ['204'];

  const { mutateAsync } = useVideoMoveCopy();
  const { colors } = useTheme();

  const { mutateAsync: mutateMultiple } = useVideosMoveCopyMultipleMutation();

  const handleSubmit = async (values: { copy: boolean; folder: FoldersV2 }) => {
    if (!videoIds.length) {
      return;
    }
    const { copy, folder: selectedFolder } = values;
    const isFolderShared = selectedFolder.userId?.toString() !== userId;

    const obj = {
      copy,
      videoFolder: isFolderShared
        ? `${SHARED_FOLDER_PREFIX}${selectedFolder.folderId}`
        : selectedFolder.name,
      successMessage: t('Video {{action}} successfully.', {
        action: copy ? t('copied') : t('moved'),
      }),
    };
    try {
      let ids: number[] = [];
      const vehicle =
        selectedVideos.length === 1
          ? (selectedVideo?.videoRequest
              ?.inventoryItem as CovideoGTMVehicleItem)
          : undefined;

      if (videoIds.length > 1) {
        for (let i = 0; i < videoIds.length; i++) {
          const id = parseInt(videoIds[i]);
          if (!isNaN(id)) ids.push(id);
        }
        await mutateMultiple({
          ...obj,
          ids,
          successMessage: t('Videos {{action}} successfully.', {
            action: copy ? t('copied') : t('moved'),
          }),
        });
        trackGTMVideoInteractionEvent?.({
          vehicle,
          action: copy
            ? GTMVideoInteractionActions.COPY
            : GTMVideoInteractionActions.MOVE,
          videoIds: videoIds.map(id => Number(id)),
        });
      } else {
        await mutateAsync({
          ...obj,
          videoId: videoIds[0],
        });
        if (selectedVideo) {
          const gtmVideoAttributes: CovideoGTMVideoAttributes = {
            videoId: Number(selectedVideo.id),
            videoType: selectedVideo?.videoRequest?.videoType,
            videoCustomerName: !!selectedVideo?.videoRequest?.customerName,
            videoLength: selectedVideo.videoLength,
            videoTitle: selectedVideo.title,
          };
          trackGTMVideoInteractionEvent?.({
            vehicle,
            action: copy
              ? GTMVideoInteractionActions.COPY
              : GTMVideoInteractionActions.MOVE,
            videoAttributes: gtmVideoAttributes,
          });
        }
      }

      handleModalClose();
    } catch (err) {
      console.log('err', err);
    }
  };

  const getMoveFieldValues = (selectedValue?: FoldersV2) => {
    if (selectedValue) {
      if (
        selectedValue.name === 'Walkaround Social' &&
        walkaroundSocialFolder.includes(resellerId)
      ) {
        return {
          copy: false,
          folder: selectedValue,
        };
      }

      if (
        selectedValue.name === 'Company' ||
        currentFolder?.folderId === DefaultFolderIds.COMPANY
      ) {
        return {
          copy: true,
          folder: selectedValue,
        };
      }

      return {
        copy: false,
        folder: selectedValue,
      };
    }

    return {
      copy: false,
      folder: { ...DEFAULT_FOLDER, userId: parseInt(userId, 10) },
    };
  };

  const getKeepCopyCondition = (values: {
    copy: boolean;
    folder?: FoldersV2;
  }) => {
    if (folder?.name === 'Company') {
      return { disabled: true, keepCopy: true };
    }

    if (currentFolder?.folderId === DefaultFolderIds.COMPANY) {
      return { disabled: true, keepCopy: true };
    }

    if (folder?.name === 'Vault' || currentFolder?.name === 'Vault') {
      return { disabled: true, keepCopy: false };
    }
    return { disabled: false, keepCopy: values.copy };
  };
  return (
    <NewModal
      hideHeader={true}
      closeModalOnBackdropOrEscPress={false}
      closeModal={() => {
        handleModalClose();
      }}
      style={{
        content: {
          padding: '24px 24px 16px 24px',
          maxWidth: '352px',
          minWidth: 'auto',
          height: '230px',
        },
      }}
    >
      <Formik
        initialValues={getMoveFieldValues(folder)}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue, submitForm }) => (
          <Flex justifyContent='space-between' height='100%'>
            <Title>{t('Select Folder:')}</Title>
            <FolderV2Dropdown
              value={{
                value: values.folder?.folderId ?? DEFAULT_FOLDER.folderId,
                label: values.folder?.name ?? DEFAULT_FOLDER.name,
              }}
              showAllFolders={false}
              showCreateButton={true}
              accessFilter={ACCESS_FILTER.EDIT}
              dropdownHeight={140}
              containerWidth='100%'
              onChange={(_, folder) => {
                if (folder) {
                  const fieldValues = getMoveFieldValues(folder);
                  setFieldValue('copy', fieldValues?.copy);
                  setFieldValue('folder', folder);
                }
              }}
            />
            <Flex margin='16px 0'>
              <FormikCheckboxField
                name='copy'
                label={t('Keep a copy of this video in the current folder')}
                onChange={() => {
                  setFieldValue(`copy`, !values.copy);
                }}
                disabled={isSubmitting || getKeepCopyCondition(values).disabled}
                extendStyles={{
                  labelStyles: {
                    fontSize: '15px',
                    fontWeight: 400,
                    color: colors.neutral[80],
                    lineHeight: '24px',
                    paddingLeft: '12px',
                  },
                }}
              />
            </Flex>
            <Flex
              justifyContent='flex-end'
              gap='8px'
              flexDirection='row'
              padding='16px 0 0 0'
            >
              <Button
                text={t('Cancel')}
                variant='secondary'
                disabled={isSubmitting}
                onClick={() => {
                  handleModalClose();
                }}
              />
              <Button
                variant='primary'
                text={
                  getKeepCopyCondition(values).keepCopy
                    ? t('Copy Video')
                    : t('Move Video')
                }
                disabled={isSubmitting}
                type='submit'
                onClick={submitForm}
              />
            </Flex>
          </Flex>
        )}
      </Formik>
    </NewModal>
  );
};
